import { useEffect, useState } from "react";
import { useSite } from "../../context/SiteContext";

const DEFAULT_PHRASES = [
    "Hello.",
    "How are you?",
    "Let's speak English.",
    "Practice every day.",
    "You can do this.",
];

const LETTERS = [
    { ch: "A", top: "6%", left: "8%", delay: 0, rotate: -8 },
    { ch: "B", top: "14%", right: "10%", delay: 0.6, rotate: 6 },
    { ch: "C", bottom: "18%", left: "4%", delay: 1.2, rotate: 10 },
    { ch: "?", bottom: "8%", right: "14%", delay: 1.8, rotate: -6 },
    { ch: "Hi", top: "44%", right: "4%", delay: 0.9, rotate: 4 },
    { ch: "Yes", bottom: "36%", left: "12%", delay: 1.5, rotate: -4 },
];

export default function HeroVisual() {
    const { content } = useSite();
    const PHRASES =
        content.hero?.typewriter && content.hero.typewriter.length > 0
            ? content.hero.typewriter
            : DEFAULT_PHRASES;

    const [idx, setIdx] = useState(0);
    const [typed, setTyped] = useState("");
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        const phrase = PHRASES[idx % PHRASES.length] || "";
        let timeout;
        if (!deleting && typed.length < phrase.length) {
            timeout = setTimeout(
                () => setTyped(phrase.slice(0, typed.length + 1)),
                70
            );
        } else if (!deleting && typed.length === phrase.length) {
            timeout = setTimeout(() => setDeleting(true), 1400);
        } else if (deleting && typed.length > 0) {
            timeout = setTimeout(
                () => setTyped(phrase.slice(0, typed.length - 1)),
                35
            );
        } else if (deleting && typed.length === 0) {
            setDeleting(false);
            setIdx((i) => (i + 1) % PHRASES.length);
        }
        return () => clearTimeout(timeout);
    }, [typed, deleting, idx, PHRASES]);

    return (
        <div
            data-testid="hero-visual"
            className="relative w-full h-[460px] md:h-[600px] overflow-hidden rounded-3xl border border-black/5 bg-gradient-to-br from-white via-[#F4F4F5] to-[#ECEDEF]"
        >
            {/* dotted grid background */}
            <div
                className="absolute inset-0 opacity-[0.35]"
                style={{
                    backgroundImage:
                        "radial-gradient(rgba(18,18,18,0.18) 1px, transparent 1px)",
                    backgroundSize: "18px 18px",
                    maskImage:
                        "radial-gradient(ellipse at center, black 40%, transparent 75%)",
                    WebkitMaskImage:
                        "radial-gradient(ellipse at center, black 40%, transparent 75%)",
                }}
            />

            {/* moving accent blobs */}
            <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-[#002FA7] blur-3xl opacity-20 animate-pulse-slow" />
            <div className="absolute -bottom-20 -left-10 w-80 h-80 rounded-full bg-[#25D366] blur-3xl opacity-10 animate-pulse-slower" />

            {/* floating letters */}
            {LETTERS.map((l, i) => (
                <div
                    key={i}
                    className="absolute select-none font-serif-display text-[#121212]/85"
                    style={{
                        top: l.top,
                        left: l.left,
                        right: l.right,
                        bottom: l.bottom,
                        transform: `rotate(${l.rotate}deg)`,
                        animation: `float-letter 7s ease-in-out ${l.delay}s infinite`,
                    }}
                >
                    <span className="inline-flex items-center justify-center bg-white border border-neutral-200 shadow-lg rounded-2xl px-4 py-2 md:px-5 md:py-3 text-2xl md:text-3xl font-semibold">
                        {l.ch}
                    </span>
                </div>
            ))}

            {/* center typewriter card */}
            <div className="absolute inset-0 flex items-center justify-center px-6">
                <div className="relative w-[88%] max-w-[420px] bg-white rounded-3xl border border-neutral-200 shadow-2xl shadow-black/5 p-8 md:p-10">
                    <div className="flex items-center gap-2 mb-5">
                        <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                        <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                        <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
                        <span className="ml-auto text-[10px] uppercase tracking-[0.2em] text-neutral-400">
                            Lesson · 01
                        </span>
                    </div>
                    <div className="text-xs uppercase tracking-[0.22em] text-neutral-500 mb-3">
                        Say it out loud
                    </div>
                    <div
                        data-testid="hero-typewriter"
                        className="font-serif-display text-3xl md:text-4xl tracking-tight min-h-[2.4em] leading-tight"
                    >
                        {typed}
                        <span className="inline-block w-[3px] h-7 md:h-9 bg-[#002FA7] align-middle ml-1 animate-blink" />
                    </div>
                    <div className="mt-6 flex items-center gap-3">
                        <span className="h-px flex-1 bg-neutral-200" />
                        <span className="text-xs text-neutral-500">EN · A1 → C1</span>
                        <span className="h-px flex-1 bg-neutral-200" />
                    </div>
                    <div className="mt-5 flex flex-wrap gap-2">
                        {["grammar", "speaking", "writing", "listening"].map((t) => (
                            <span
                                key={t}
                                className="text-[11px] uppercase tracking-wider px-3 py-1 rounded-full bg-neutral-100 text-neutral-700 border border-neutral-200"
                            >
                                {t}
                            </span>
                        ))}
                    </div>
                </div>
            </div>

            {/* marquee strip bottom */}
            <div className="absolute left-0 right-0 bottom-0 border-t border-neutral-200 bg-white/70 backdrop-blur-md overflow-hidden">
                <div className="flex whitespace-nowrap py-3 animate-marquee">
                    {Array.from({ length: 2 }).map((_, k) => (
                        <div key={k} className="flex shrink-0">
                            {[
                                "fluency",
                                "vocabulary",
                                "pronunciation",
                                "confidence",
                                "exam prep",
                                "conversation",
                                "writing",
                                "listening",
                            ].map((w, i) => (
                                <span
                                    key={`${k}-${i}`}
                                    className="px-6 text-sm uppercase tracking-[0.18em] text-neutral-500"
                                >
                                    {w}
                                    <span className="ml-6 text-[#002FA7]">✦</span>
                                </span>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
